import { reactive, ref, watch } from 'vue'
import { debounce, makeInstanceFactory } from '@zoomcatalog/shared'
import { translate } from '@/i18n'
import type { BasicPage, Page } from '@/store/interfaces'
import {
	Command,
	MultiEventsCommand,
	UpdatePagesIndexesCommand,
} from '@/store/plugins/autoSave/types'
import { useCommit, useSelector } from '@/store/rootStore'
import { storageInstances } from '@/utils/storageIntances'
import { useGetPagesByIndexProjectId } from './RequestUtils/usePage'
import { usePageSettingsStore } from './StoreUtils/usePageSettingsStore'
import { useProjectStore } from './StoreUtils/useProjectStore'
import { useNotification } from './useNotification'
import { getNormalizedPage } from './usePagination'

const [
	useMakePaginationFixerInstance,
	usePaginationFixerInstance,
	usePaginationFixerHandlers,
] = makeInstanceFactory('pagination-fixer', () => {
	const { enqueueNotification } = useNotification()
	const commit = useCommit()
	const isSending = useSelector((state) => state.autoSave.isSending)
	const { projectData } = useProjectStore()
	const pageSettingsStore = usePageSettingsStore()
	const updateRequiredSpreadPages = ref<BasicPage[]>()

	/**
	 * Handlers
	 */
	function hasRepeatedBasicPages(pages: BasicPage[]) {
		const uniquePageIds = new Set(pages.map((page) => page.index))

		return uniquePageIds.size < pages.length
	}

	function hasInvalidPageNumbersOrder(pages: BasicPage[]) {
		return !pages.every((page, index) => {
			const expectedPageNumber = index + 1
			return page.index === expectedPageNumber
		})
	}

	function filterUpdateRequiredSpreadPages(basicPages: BasicPage[]) {
		return basicPages.reduce<typeof basicPages[number][]>(
			(group, page, index) => {
				const prevPage = basicPages?.[index - 1]
				const currentPage = page
				const nextPage = basicPages?.[index + 1]

				if (currentPage.spread_page === null && nextPage?.spread_page === 'right')
					return [...group, { ...currentPage, spread_page: 'left' as const }]

				if (prevPage?.spread_page === null && currentPage.spread_page === 'right')
					return group

				if (!currentPage.spread_page) return group

				if (currentPage.spread_page === 'left' && nextPage?.spread_page === 'right')
					return group

				if (prevPage?.spread_page === 'left' && currentPage.spread_page === 'right')
					return group

				return [...group, { ...currentPage, spread_page: null }]
			},
			[]
		)
	}

	function sortPages<T extends BasicPage[] | Page[]>(pages: T) {
		return [...pages].sort((a, b) => a.index - b.index)
	}

	function verifyBasicPages(context: {
		projectId: string
		basicPages: BasicPage[]
	}) {
		const commandId = ref(1)
		const commandsToFixProject = ref<Command[]>([])
		const sortedBasicPages = sortPages(context.basicPages)

		if (
			!hasRepeatedBasicPages(sortedBasicPages) &&
			!hasInvalidPageNumbersOrder(sortedBasicPages)
		) {
			updateRequiredSpreadPages.value =
				filterUpdateRequiredSpreadPages(sortedBasicPages)
			return
		}

		const updatePagesIndexes: UpdatePagesIndexesCommand = {
			timestamp: Date.now(),
			action: 'update_pages_indexes',
			endpoint: 'send_changes',
			resource: 'flyer',
			token: storageInstances.userToken.get(),
			id: commandId.value,
			data: {
				project_id: context.projectId,
				pages: Object.values(sortedBasicPages).reduce(
					(obj, page, index) => ({ ...obj, [page.page_id]: index + 1 }),
					{}
				),
			},
		}
		commandId.value += 1
		commandsToFixProject.value.push(updatePagesIndexes)

		sendCommandsAndReload(commandsToFixProject.value)
	}

	async function verifyAndFixSpreadPages(context: {
		projectId: string
		pages: Page[]
	}) {
		const commandId = ref(1)
		const commandsToFixProject = ref<Command[]>([])
		const pages = ref<Page[]>([])

		if (!updateRequiredSpreadPages.value?.length) return false

		// we need the complete pages data in order to perform the update
		// some of them might be under the context.pages already
		// but some others might be missing, so we need to fetch them
		const missingPagesIndexes = updateRequiredSpreadPages.value
			.map((page) => page.index)
			.filter((index) => {
				const found = context.pages.find((p) => p.index === index)
				if (found) {
					pages.value.push(found)
				}
				return !found
			})

		if (missingPagesIndexes.length) {
			const pagesQuery = useGetPagesByIndexProjectId({
				projectId: context.projectId,
				limit: 15,
				indexMap: missingPagesIndexes,
				enabled: true,
			})

			await pagesQuery.fetchNextPage()
			while (pagesQuery.hasNextPage?.value) {
				await pagesQuery.fetchNextPage()
			}

			pagesQuery.data?.value?.pages.forEach((pageBatch) => {
				pageBatch.forEach((page) => {
					if (!pages.value.find((p) => p.page_id === page.page_id)) {
						pages.value.push(page)
					}
				})
			})
		}

		updateRequiredSpreadPages.value.forEach((updateRequiredSpreadPage) => {
			const page = pages.value.find(
				(p) => p.index === updateRequiredSpreadPage.index
			)
			if (!page) return

			commandsToFixProject.value.push({
				timestamp: Date.now(),
				action: 'update_page',
				endpoint: 'send_changes',
				resource: 'flyer',
				id: commandId.value,
				token: storageInstances.userToken.get(),
				data: {
					cover_image: page.cover_image,
					cover_image_2: page.cover_image_2 ?? '',
					title: page.title,
					index: page.index,
					layout_id: page.layout_id,
					page_id: page.page_id,
					project_id: projectData.value.project_id,
					settings: {
						...page.settings,
						feature: {
							...page.settings?.feature,
							spreadPage: updateRequiredSpreadPage.spread_page,
						},
					},
				},
			})
		})

		if (!commandsToFixProject.value.length) return false

		const multiCommand: MultiEventsCommand = {
			timestamp: Date.now(),
			action: 'multi_events',
			endpoint: 'send_changes',
			id: 1,
			token: storageInstances.userToken.get(),
			commands: commandsToFixProject.value,
		}

		sendCommandsAndReload([multiCommand])
		return true
	}

	function sendCommandsAndReload(commands: Command[]) {
		enqueueNotification({
			message: translate('common.PENDING_UPDATE_DETECTED'),
			color: 'success',
			closable: false,
			delay: 0,
		})

		commit({
			type: 'autoSave/sendMultiCommands',
			payload: commands,
		})

		watch(
			isSending,
			(newIsSending) => {
				if (!newIsSending && !projectData.value.is_locked) {
					debouncePageReload()
				}
			},
			{ immediate: true }
		)
	}

	const debouncePageReload = debounce(() => {
		window.location.reload()
	}, 5000)

	function addSpreadPageDefaultSetting(pages: Page[]) {
		pages.forEach((page) => {
			if (page.settings?.feature && 'spreadPage' in page.settings.feature) return

			const normalizedPage = getNormalizedPage(page, projectData.value.currency)

			pageSettingsStore.setPageSettings(
				{
					...normalizedPage.settings,
					feature: {
						...normalizedPage.settings?.feature,
						spreadPage: null,
					},
				},
				normalizedPage
			)
		})
	}

	return [
		reactive({}),
		{
			verifyBasicPages,
			verifyAndFixSpreadPages,
			addSpreadPageDefaultSetting,
		},
	]
})

export {
	useMakePaginationFixerInstance,
	usePaginationFixerInstance,
	usePaginationFixerHandlers,
}
